// src/pages/dashboard/Contacts.tsx
import React, { useMemo, useState } from "react";
import {
  Loader2,
  Search,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  Users,
  TrendingUp,
  MousePointerClick,
  ShoppingBag,
  Calendar,
  Tag,
  ChevronDown,
  ChevronUp,
  X,
  BarChart2,
  Inbox,
  MessageSquarePlus,
  Send,
  CheckCircle2,
} from "lucide-react";
import {
  useGetContactsQuery,
  Contact,
  ContactType,
} from "../../app/api/contactApi";
import { useSendBulkEmailMutation } from "../../app/api/adminApi";
import ContactWorkspace from "../../components/dashboard/ContactWorkspace";

/* ─── helpers ─────────────────────────────────────────────────── */
function cn(...cls: Array<string | false | null | undefined>) {
  return cls.filter(Boolean).join(" ");
}

const fmtDate = (d?: string | Date | null) => {
  if (!d) return null;
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
};

const fmtDateShort = (d?: string | Date | null) => {
  if (!d) return null;
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(dt);
};

const TYPE_META: Record<string, { label: string; color: string; bg: string }> =
  {
    lead: {
      label: "Lead",
      color: "text-violet-700",
      bg: "bg-violet-50 border-violet-200",
    },
    custom_funnel: {
      label: "Custom Funnel",
      color: "text-sky-700",
      bg: "bg-sky-50 border-sky-200",
    },
    faith_that_works: {
      label: "Faith That Works",
      color: "text-emerald-700",
      bg: "bg-emerald-50 border-emerald-200",
    },
    monetize_knowledge: {
      label: "Monetize Knowledge",
      color: "text-amber-700",
      bg: "bg-amber-50 border-amber-200",
    },
    wealth_method: {
      label: "Wealth Method",
      color: "text-rose-700",
      bg: "bg-rose-50 border-rose-200",
    },
  };

const TypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const m = TYPE_META[type] || {
    label: type,
    color: "text-slate-600",
    bg: "bg-slate-50 border-slate-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold tracking-wide",
        m.color,
        m.bg,
      )}
    >
      {m.label}
    </span>
  );
};

const initials = (first: string, last: string) => {
  const f = (first || "").trim()[0] || "";
  const l = (last || "").trim()[0] || "";
  return (f + l).toUpperCase() || "?";
};

const AVATAR_COLORS = [
  "bg-violet-500",
  "bg-sky-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-indigo-500",
  "bg-teal-500",
];
const avatarColor = (email: string) =>
  AVATAR_COLORS[(email?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

/* ─── expanded detail panel ───────────────────────────────────── */
const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs font-medium text-slate-500 shrink-0 w-32">
      {label}
    </span>
    <span className="text-xs text-slate-800 text-right">
      {value || <span className="text-slate-400">—</span>}
    </span>
  </div>
);

const StatChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  accent: string;
}> = ({ icon, label, value, accent }) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center rounded-xl border p-3 gap-1",
      accent,
    )}
  >
    <div className="text-current opacity-60">{icon}</div>
    <span className="text-lg font-bold text-slate-900 leading-none">
      {value}
    </span>
    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
      {label}
    </span>
  </div>
);

/* ─── contact card ─────────────────────────────────────────────── */
const ContactCard: React.FC<{
  c: Contact;
  onFollowUp: (c: Contact) => void;
  selected: boolean;
  onToggleSelect: (c: Contact) => void;
}> = ({ c, onFollowUp, selected, onToggleSelect }) => {
  const [expanded, setExpanded] = useState(false);

  const name =
    (c.fullName || `${c.firstName || ""} ${c.lastName || ""}`).trim() ||
    "Unnamed Contact";
  const lastActivity = c.metrics?.lastActivityAt ?? null;
  const initStr = initials(c.firstName || "", c.lastName || "");
  const colorCls = avatarColor(c.email || "");

  const purchases = (c.purchases || []) as Array<Record<string, any>>;
  const events = (c.events || []) as Array<Record<string, any>>;

  return (
    <div
      className={cn(
        "group rounded-2xl border bg-white shadow-sm transition-all duration-200",
        selected
          ? "border-violet-400 ring-1 ring-violet-200 shadow-md"
          : expanded
            ? "border-slate-300 shadow-md"
            : "border-slate-200 hover:border-slate-300 hover:shadow-md",
      )}
    >
      {/* ── main row ── */}
      <div className="flex items-start gap-4 p-5">
        {/* select checkbox */}
        <input
          type="checkbox"
          checked={selected}
          disabled={!c.email}
          onChange={() => onToggleSelect(c)}
          title={c.email ? "Select for bulk email" : "No email on this contact"}
          className="mt-3.5 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-violet-600 focus:ring-violet-200 disabled:cursor-not-allowed disabled:opacity-40"
        />
        {/* avatar */}
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white",
            colorCls,
          )}
        >
          {initStr}
        </div>

        {/* core info */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-slate-900 leading-tight">
                {name}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {lastActivity
                  ? `Active ${fmtDate(lastActivity)}`
                  : "No recent activity"}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              {(c.types || []).map((t) => (
                <TypeBadge key={t} type={t} />
              ))}
            </div>
          </div>

          {/* email + phone */}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {c.email && (
              <a
                href={`mailto:${c.email}`}
                className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-violet-600 transition-colors"
              >
                <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                {c.email}
              </a>
            )}
            {c.phone && (
              <a
                href={`tel:${c.phone}`}
                className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-violet-600 transition-colors"
              >
                <Phone className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                {c.phone}
              </a>
            )}
          </div>

          {/* metrics strip */}
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              {
                label: "Opens",
                value: c.metrics?.emailOpens ?? 0,
                accent: "border-violet-100 bg-violet-50",
                icon: <Inbox className="h-4 w-4 text-violet-400" />,
              },
              {
                label: "Clicks",
                value: c.metrics?.emailClicks ?? 0,
                accent: "border-sky-100 bg-sky-50",
                icon: <MousePointerClick className="h-4 w-4 text-sky-400" />,
              },
              {
                label: "Purchases",
                value: c.metrics?.purchases ?? 0,
                accent: "border-emerald-100 bg-emerald-50",
                icon: <ShoppingBag className="h-4 w-4 text-emerald-400" />,
              },
              {
                label: "Webinars",
                value: c.metrics?.webinarsEnrolled ?? 0,
                accent: "border-amber-100 bg-amber-50",
                icon: <BarChart2 className="h-4 w-4 text-amber-400" />,
              },
            ].map((s) => (
              <StatChip
                key={s.label}
                icon={s.icon}
                label={s.label}
                value={s.value}
                accent={s.accent}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── action row ── */}
      <div className="grid grid-cols-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => onFollowUp(c)}
          className="flex items-center justify-center gap-1.5 border-r border-slate-100 py-2 text-xs font-semibold text-violet-600 hover:bg-violet-50 transition-colors rounded-bl-2xl"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" /> Follow up
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors rounded-br-2xl"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" /> Collapse
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" /> View details
            </>
          )}
        </button>
      </div>

      {/* ── expanded content ── */}
      {expanded && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* tags */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <Tag className="h-3.5 w-3.5" /> Tags
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(c.tags || []).length ? (
                (c.tags || []).map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-white border border-slate-200 px-2.5 py-0.5 text-xs text-slate-700 font-medium"
                  >
                    {t}
                  </span>
                ))
              ) : (
                <span className="text-xs text-slate-400">No tags</span>
              )}
            </div>
          </div>

          {/* funnels */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <TrendingUp className="h-3.5 w-3.5" /> Funnels
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(c.funnels || []).length ? (
                (c.funnels || []).map((f) => (
                  <span
                    key={f}
                    className="rounded-full bg-sky-50 border border-sky-200 px-2.5 py-0.5 text-xs text-sky-700 font-medium"
                  >
                    {f}
                  </span>
                ))
              ) : (
                <span className="text-xs text-slate-400">No funnels</span>
              )}
            </div>
          </div>

          {/* statuses */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Statuses
            </p>
            <div className="space-y-0">
              {c.statuses && Object.keys(c.statuses).length ? (
                Object.entries(c.statuses).map(([k, v]) => (
                  <DetailRow
                    key={k}
                    label={TYPE_META[k]?.label || k}
                    value={
                      <span className="inline-block rounded-full bg-white border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {v}
                      </span>
                    }
                  />
                ))
              ) : (
                <span className="text-xs text-slate-400">No statuses</span>
              )}
            </div>
          </div>

          {/* consent */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Consent
            </p>
            {[
              { key: "email", label: "Email marketing" },
              { key: "sms", label: "SMS marketing" },
            ].map(({ key, label }) => {
              const val = (c.consent as any)?.[key];
              return (
                <div
                  key={key}
                  className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0"
                >
                  <span className="text-xs text-slate-600">{label}</span>
                  <span
                    className={cn(
                      "text-xs font-semibold rounded-full px-2 py-0.5",
                      val === true
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : val === false
                          ? "bg-red-50 text-red-600 border border-red-200"
                          : "bg-slate-100 text-slate-400 border border-slate-200",
                    )}
                  >
                    {val === true
                      ? "Opted in"
                      : val === false
                        ? "Opted out"
                        : "Unknown"}
                  </span>
                </div>
              );
            })}
          </div>

          {/* purchases */}
          {purchases.length > 0 && (
            <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <ShoppingBag className="h-3.5 w-3.5" /> Purchases
              </p>
              <div className="space-y-2">
                {purchases.map((p, i) => (
                  <div
                    key={i}
                    className="rounded-lg bg-white border border-slate-200 px-3 py-2 flex flex-wrap items-center justify-between gap-2"
                  >
                    <span className="text-xs font-medium text-slate-700">
                      {p.product?.name || p.product || "Purchase"}
                    </span>
                    <div className="flex items-center gap-2">
                      {p.status && (
                        <span
                          className={cn(
                            "text-xs font-semibold rounded-full px-2 py-0.5 border",
                            p.status === "paid"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-50 text-slate-500 border-slate-200",
                          )}
                        >
                          {p.status}
                        </span>
                      )}
                      {p.orderId && (
                        <span className="text-xs text-slate-400">
                          #{p.orderId}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* events */}
          {events.length > 0 && (
            <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Calendar className="h-3.5 w-3.5" /> Events
              </p>
              <div className="space-y-2">
                {events.map((e, i) => (
                  <div
                    key={i}
                    className="rounded-lg bg-white border border-slate-200 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-700">
                        {e.venue || e.eventCity || "Event"}
                      </span>
                      <div className="flex items-center gap-2">
                        {e.status && (
                          <span className="text-xs text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                            {e.status}
                          </span>
                        )}
                        {e.eventDate && (
                          <span className="text-xs text-slate-400">
                            {fmtDateShort(e.eventDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    {e.eventCity && e.venue && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {e.eventCity}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* timestamps */}
          <div className="md:col-span-2 text-xs text-slate-400 flex flex-wrap gap-4">
            {c.createdAt && <span>Created: {fmtDate(c.createdAt)}</span>}
            {c.updatedAt && <span>Updated: {fmtDate(c.updatedAt)}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── type filter tabs ─────────────────────────────────────────── */
const typeOptions: Array<{ value: ContactType; label: string }> = [
  { value: "all", label: "All" },
  { value: "lead", label: "Leads" },
  { value: "custom_funnel", label: "Custom Funnels" },
  { value: "faith_that_works", label: "Faith That Works" },
  { value: "monetize_knowledge", label: "Monetize Knowledge" },
  { value: "wealth_method", label: "Wealth Method" },
];

/* ─── bulk email compose modal ─────────────────────────────────── */
const BulkEmailModal: React.FC<{
  recipients: Array<{ email: string; name?: string }>;
  onClose: () => void;
  onSent: () => void;
}> = ({ recipients, onClose, onSent }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<{
    sent: number;
    failed: number;
    failures: Array<{ email: string; error: string }>;
  } | null>(null);
  const [error, setError] = useState("");
  const [sendBulk, { isLoading }] = useSendBulkEmailMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res = await sendBulk({ recipients, subject, message }).unwrap();
      setResult(res.data);
      onSent();
    } catch (err: any) {
      setError(
        err?.data?.error || err?.error || "Failed to send. Please try again.",
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100">
              <Mail className="h-4 w-4 text-violet-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Email {recipients.length} recipient
                {recipients.length === 1 ? "" : "s"}
              </h2>
              <p className="text-xs text-slate-400">
                Sends individually — recipients won't see each other.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="px-6 py-6">
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              Sent to {result.sent} of {result.sent + result.failed} recipient
              {result.sent + result.failed === 1 ? "" : "s"}.
            </div>
            {result.failed > 0 && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <p className="mb-1 font-semibold">
                  {result.failed} failed:
                </p>
                <ul className="max-h-32 space-y-0.5 overflow-y-auto">
                  {result.failures.map((f) => (
                    <li key={f.email}>
                      {f.email} — {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={onClose}
              className="mt-5 w-full rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5">
            {error && (
              <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <div className="mb-3 max-h-16 overflow-y-auto rounded-lg bg-slate-50 p-2 text-xs text-slate-500">
              {recipients
                .slice(0, 30)
                .map((r) => r.name || r.email)
                .join(", ")}
              {recipients.length > 30 && ` +${recipients.length - 30} more`}
            </div>
            <input
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="mb-3 w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-violet-300"
            />
            <textarea
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              placeholder={"Hi {first name},\n\nWrite your message…"}
              className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-violet-300"
            />
            <p className="mt-1 text-xs text-slate-400">
              Each contact is greeted by their own name. Logged to every
              contact's timeline.
            </p>
            <button
              type="submit"
              disabled={isLoading || !subject.trim() || !message.trim()}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> Send to {recipients.length}
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ─── page ─────────────────────────────────────────────────────── */
const ContactsPage: React.FC = () => {
  const [type, setType] = useState<ContactType>("all");
  const [workspace, setWorkspace] = useState<Contact | null>(null);
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [showBulk, setShowBulk] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const limit = 25;

  const { data, isLoading, isFetching, error } = useGetContactsQuery(
    { type, q: q.trim() || undefined, page, limit },
    { refetchOnMountOrArgChange: true },
  );

  const contacts = data?.data?.contacts ?? [];
  const pagination = data?.data?.pagination;

  const errMsg = useMemo(() => {
    const anyErr: any = error;
    return anyErr?.data?.error || anyErr?.error || anyErr?.message || null;
  }, [error]);

  const clearSearch = () => {
    setQ("");
    setPage(1);
  };

  const toggleSelect = (c: Contact) => {
    if (!c.email) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(c.email)) next.delete(c.email);
      else
        next.set(
          c.email,
          (c.fullName || `${c.firstName || ""} ${c.lastName || ""}`).trim(),
        );
      return next;
    });
  };

  const emailableOnPage = contacts.filter((c) => c.email);
  const allOnPageSelected =
    emailableOnPage.length > 0 &&
    emailableOnPage.every((c) => selected.has(c.email));

  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        emailableOnPage.forEach((c) => next.delete(c.email));
      } else {
        emailableOnPage.forEach((c) =>
          next.set(
            c.email,
            (c.fullName || `${c.firstName || ""} ${c.lastName || ""}`).trim(),
          ),
        );
      }
      return next;
    });
  };

  const recipients = Array.from(selected.entries()).map(([email, name]) => ({
    email,
    name,
  }));

  return (
    <div className="min-h-screen bg-slate-50/60 px-4 py-8 md:px-8">
      <div className="mx-auto max-w-4xl">
        {/* ── header ── */}
        <div className="mb-7">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900">
              <Users className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Contacts
            </h1>
            {pagination && (
              <span className="ml-1 rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                {pagination.total.toLocaleString()}
              </span>
            )}
            {isFetching && !isLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400 ml-1" />
            )}
          </div>
          <p className="text-sm text-slate-500 ml-12">
            Unified view across all leads, funnels, events, and purchases.
          </p>
        </div>

        {/* ── search + type tabs ── */}
        <div className="mb-6 flex flex-col gap-3">
          {/* search */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name, email, phone, or funnel…"
              className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-10 text-sm text-slate-900 shadow-sm outline-none transition focus:border-violet-300 focus:ring-2 focus:ring-violet-100 placeholder:text-slate-400"
            />
            {q && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* type tabs */}
          <div className="flex flex-wrap gap-1.5">
            {typeOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  setType(o.value);
                  setPage(1);
                }}
                className={cn(
                  "rounded-xl border px-3.5 py-1.5 text-xs font-semibold transition-all",
                  type === o.value
                    ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── content ── */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm font-medium">Loading contacts…</p>
          </div>
        ) : errMsg ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            <p className="font-semibold mb-0.5">Something went wrong</p>
            {errMsg}
          </div>
        ) : (
          <>
            {/* result count + select all */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              {pagination && (
                <p className="text-sm text-slate-500">
                  Showing{" "}
                  <span className="font-semibold text-slate-800">
                    {(page - 1) * limit + 1}–
                    {Math.min(page * limit, pagination.total)}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-slate-800">
                    {pagination.total.toLocaleString()}
                  </span>{" "}
                  contacts
                  {q && (
                    <>
                      {" "}
                      for{" "}
                      <span className="font-semibold text-slate-800">
                        "{q}"
                      </span>
                    </>
                  )}
                </p>
              )}
              {emailableOnPage.length > 0 && (
                <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-700">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-200"
                  />
                  Select all on page
                </label>
              )}
            </div>

            {/* cards */}
            <div className="space-y-3">
              {contacts.length ? (
                contacts.map((c) => (
                  <ContactCard
                    key={c.id}
                    c={c}
                    onFollowUp={setWorkspace}
                    selected={!!c.email && selected.has(c.email)}
                    onToggleSelect={toggleSelect}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-slate-500">
                  <Users className="h-10 w-10 text-slate-300" />
                  <div className="text-center">
                    <p className="font-semibold text-slate-700">
                      No contacts found
                    </p>
                    <p className="mt-1 text-sm">
                      {q
                        ? "Try a different search term."
                        : "No contacts match the selected filter."}
                    </p>
                  </div>
                  {q && (
                    <button
                      onClick={clearSearch}
                      className="mt-1 text-sm font-semibold text-violet-600 hover:text-violet-700 underline underline-offset-2"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={cn(
                    "flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-semibold shadow-sm transition",
                    page <= 1
                      ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300",
                  )}
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>

                <div className="text-sm text-slate-600">
                  Page{" "}
                  <span className="font-bold text-slate-900">
                    {pagination.page}
                  </span>
                  <span className="text-slate-400"> / </span>
                  <span className="font-bold text-slate-900">
                    {pagination.totalPages}
                  </span>
                </div>

                <button
                  type="button"
                  disabled={page >= pagination.totalPages}
                  onClick={() =>
                    setPage((p) => Math.min(pagination.totalPages, p + 1))
                  }
                  className={cn(
                    "flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-semibold shadow-sm transition",
                    page >= pagination.totalPages
                      ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300",
                  )}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {workspace && (
        <ContactWorkspace
          email={workspace.email}
          name={
            workspace.fullName ||
            `${workspace.firstName || ""} ${workspace.lastName || ""}`.trim()
          }
          onClose={() => setWorkspace(null)}
        />
      )}

      {/* ── floating selection toolbar ── */}
      {selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
          <div className="flex w-full max-w-md items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-xl">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
              {selected.size}
            </span>
            <span className="text-sm font-medium text-slate-700">
              selected
            </span>
            <button
              onClick={() => setSelected(new Map())}
              className="ml-auto rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-50"
            >
              Clear
            </button>
            <button
              onClick={() => setShowBulk(true)}
              className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Send className="h-4 w-4" /> Email
            </button>
          </div>
        </div>
      )}

      {showBulk && (
        <BulkEmailModal
          recipients={recipients}
          onClose={() => setShowBulk(false)}
          onSent={() => setSelected(new Map())}
        />
      )}
    </div>
  );
};

export default ContactsPage;
